import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { useHaptics } from '../../utils/haptics'
import { useMoney } from '../../hooks/usePublicSettings'
import { bookingApi, moneyRange } from './bookingApi'
import PageLayout from '../common/PageLayout'
import Card from '../common/Card'
import StatusIndicator from '../common/StatusIndicator'

// Icons
function ArrowLeftIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M19 12H5M12 19l-7-7 7-7"/>
    </svg>
  )
}

function StarIcon({ filled = false, ...props }) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/>
    </svg>
  )
}

function MapPinIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
      <circle cx="12" cy="10" r="3"/>
    </svg>
  )
}

function CheckCircleIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
      <path d="M9 11l3 3L22 4"/>
    </svg>
  )
}

function FilterIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46"/>
    </svg>
  )
}

// Category Icons
function PlumbingIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M14 7l-6.5 6.5a2.12 2.12 0 0 0 3 3L17 10"/>
      <path d="M13 6L6 13l-3 3 3 3 3-3 7-7"/>
    </svg>
  )
}

function ElectricalIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <polygon points="13,2 3,14 12,14 11,22 21,10 12,10"/>
    </svg>
  )
}

function PaintingIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M18.37 2.63 14 7l-1.59-1.59a2 2 0 0 0-2.82 0L8 7l9 9 1.59-1.59a2 2 0 0 0 0-2.82L17 10l4.37-4.37a2.12 2.12 0 1 0-3-3Z"/>
      <path d="M9 8c-2 3-4 3.5-7 4l8 10c2-1 6-5 6-7"/>
    </svg>
  )
}

function CarpentryIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
    </svg>
  )
}

function ConsultantIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  )
}

function AllIcon(props) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <rect x="3" y="3" width="7" height="7"/>
      <rect x="14" y="3" width="7" height="7"/>
      <rect x="14" y="14" width="7" height="7"/>
      <rect x="3" y="14" width="7" height="7"/>
    </svg>
  )
}

const tradeName = t => t.replace(/[_-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase())

export default function ProfessionalsList() {
  const navigate = useNavigate()
  const location = useLocation()
  const haptics = useHaptics()
  const money = useMoney()
  const [categories, setCategories] = useState([{ id: 'all', name: 'All Services', count: null }])
  const [searchParams] = useSearchParams()
  const [professionals, setProfessionals] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all')
  
  // Get service filter from navigation state (when coming from ServiceCards)
  const navigationState = location.state || {}
  const serviceFilter = navigationState.serviceFilter

  useEffect(() => {
    loadProfessionals()
  }, [selectedCategory])

  // Categories = trades with at least one bookable professional (real counts)
  useEffect(() => {
    bookingApi.trades()
      .then(t => setCategories([
        { id: 'all', name: 'All Services', count: t.total },
        ...t.trades.map(x => ({ id: x.trade, name: tradeName(x.trade), count: x.pros }))
      ]))
      .catch(() => {})
  }, [])

  const loadProfessionals = async () => {
    setLoading(true)
    try {
      const data = await bookingApi.professionals(selectedCategory !== 'all' ? { trade: selectedCategory } : {})
      setProfessionals(data.professionals || [])
    } catch (error) {
      console.error('Error loading professionals:', error)
      setProfessionals([])
    } finally {
      setLoading(false)
    }
  }

  const handleRefresh = async () => {
    haptics.light()
    await loadProfessionals()
  }

  const getCategoryIcon = (categoryId) => {
    switch (categoryId) {
      case 'all': return <AllIcon />
      case 'plumbing': return <PlumbingIcon />
      case 'electrical': return <ElectricalIcon />
      case 'hvac': return <ConsultantIcon />
      case 'painting': return <PaintingIcon />
      case 'carpentry': return <CarpentryIcon />
      default: return <AllIcon />
    }
  }

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <StarIcon key={i} filled={i < Math.floor(rating)} className="text-yellow-400" />
    ))
  }

  const handleProfessionalClick = (professionalId) => {
    haptics.light()
    navigate(`/professionals/${professionalId}`)
  }

  const handleBookNow = (professionalId) => {
    haptics.light()
    // The trade being browsed prices the visit (otherwise the server uses the pro's first trade)
    navigate(`/booking/datetime/${professionalId}`, { state: selectedCategory !== 'all' ? { trade: selectedCategory } : {} })
  }

  // ✅ FIX: Remove filter icon as requested
  const headerAction = null

  const getPageTitle = () => {
    return "Professionals"
  }

  const getPageSubtitle = () => {
    if (serviceFilter) {
      return serviceFilter.title
    }
    return "Browse and book trusted service providers"
  }

  const getPageDetails = () => {
    if (serviceFilter) {
      return loading ? null : `${professionals.length} professionals available`
    }
    return null
  }

  const backButton = (
    <button 
      onClick={() => {
        haptics.light()
        navigate(-1)
      }}
      className="p-2 hover:bg-slate-100 rounded-xl touch-manipulation"
    >
      <ArrowLeftIcon />
    </button>
  )

  return (
    <div className="min-h-screen bg-white">
      {/* Custom Header with Back Navigation */}
      <div className="sticky top-0 z-20 bg-white px-4 py-4 shadow-lg">
        <div className="flex items-center gap-3">
          {backButton}
          <div className="flex-1">
            <h1 className="text-xl font-bold text-slate-900">{getPageTitle()}</h1>
            {getPageSubtitle() && <p className="text-sm text-slate-500 mt-1">{getPageSubtitle()}</p>}
            {getPageDetails() && <p className="text-xs text-slate-400 mt-1">{getPageDetails()}</p>}
          </div>
          {headerAction && <div>{headerAction}</div>}
        </div>
      </div>

      <div className="px-4 py-6 space-y-4 pb-20">
        {/* Category Filter */}
        <div className="flex gap-2 overflow-x-auto pb-2 mb-4">
          {categories.map(category => (
            <button
              key={category.id}
              onClick={() => {
                haptics.light()
                setSelectedCategory(category.id)
              }}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium whitespace-nowrap touch-manipulation transition-all ${
                selectedCategory === category.id
                  ? 'bg-slate-900 text-white shadow-lg'
                  : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 hover:shadow-md'
              }`}
            >
              {getCategoryIcon(category.id)}
              <span>{category.name}</span>
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                selectedCategory === category.id
                  ? 'bg-slate-700 text-slate-200'
                  : 'bg-slate-100 text-slate-600'
              }`}>
                {category.count ?? '—'}
              </span>
            </button>
          ))}
        </div>

        {/* Professionals List */}
        <div className="space-y-4">
          {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}>
              <div className="animate-pulse">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 bg-slate-200 rounded-full"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-slate-200 rounded w-3/4"></div>
                    <div className="h-3 bg-slate-200 rounded w-1/2"></div>
                    <div className="h-3 bg-slate-200 rounded w-2/3"></div>
                  </div>
                </div>
              </div>
            </Card>
          ))
        ) : professionals.length === 0 ? (
          <Card>
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🔍</span>
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">No professionals found</h3>
              <p className="text-slate-600 text-sm">Try selecting a different category or check back later.</p>
            </div>
          </Card>
        ) : (
          professionals.map((professional) => (
            <Card key={professional.id} hover onClick={() => handleProfessionalClick(professional.id)}>
              <div className="flex items-start gap-4">
                {/* Professional Avatar - ✅ FIX: Made smaller (w-14 h-14 instead of w-16 h-16) */}
                <div className="w-14 h-14 bg-slate-200 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-base font-semibold text-slate-600">
                    {(professional.name || '').split(' ').map(n => n[0]).join('')}
                  </span>
                </div>

                {/* Professional Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-slate-900">{professional.name}</h3>
                        <StatusIndicator 
                          status={professional.onlineStatus} 
                          size="xs"
                          showText={false}
                          showResponseTime={false}
                        />
                        {professional.onlineStatus === 'online' && (
                          <span className="text-xs text-slate-500">
                            {professional.responseTime}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-600">{professional.company}</p>
                    </div>
                    <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                      <StarIcon filled className="text-yellow-400" />
                      <span className="text-sm font-medium text-slate-900">
                        {professional.rating != null ? Number(professional.rating).toFixed(1) : '—'}
                      </span>
                      <span className="text-sm text-slate-500">
                        ({professional.reviewCount})
                      </span>
                    </div>
                  </div>

                  {/* Specialties */}
                  <div className="flex flex-wrap gap-1 mb-3">
                    {(professional.trades || []).map((specialty, index) => (
                      <span 
                        key={index}
                        className="px-2 py-1 bg-slate-100 text-slate-700 rounded-md text-xs font-medium"
                      >
                        {specialty}
                      </span>
                    ))}
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-4 text-xs text-slate-600 mb-3">
                    <div className="flex items-center gap-1">
                      <span>{professional.yearsExperience != null ? `${professional.yearsExperience} years` : '—'}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span>{professional.completedJobs} jobs</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span>{professional.hourlyRate ? `${moneyRange(money, professional.hourlyRate)}/hour` : '—'}</span>
                    </div>
                  </div>

                  {/* Location & Badges */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 text-sm text-slate-600">
                      <MapPinIcon />
                      <span>{professional.location || '—'}</span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {professional.verified && (
                        <div className="flex items-center gap-1 text-green-600">
                          <CheckCircleIcon className="w-3 h-3" />
                          <span className="text-xs font-medium">Verified</span>
                        </div>
                      )}
                      {professional.emergencyAvailable && (
                        <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-md">
                          24/7
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleBookNow(professional.id)
                      }}
                      className="flex-1 py-2 px-4 bg-slate-900 text-white rounded-xl font-medium hover:bg-slate-800 touch-manipulation text-sm"
                    >
                      Book Now
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleProfessionalClick(professional.id)
                      }}
                      className="px-4 py-2 border border-slate-300 rounded-xl text-slate-700 font-medium hover:bg-slate-50 touch-manipulation text-sm"
                    >
                      View Profile
                    </button>
                  </div>
                </div>
              </div>
            </Card>
          ))
        )}
        </div>
      </div>
    </div>
  )
}
